import React, { useEffect, useRef, useState, useCallback } from 'react';

export default function CursorTrail() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const isMouseMovingRef = useRef(false);
  const mouseStopTimeoutRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Enhanced Particle class with more effects
    class Particle {
      constructor(x, y, vx, vy, type = 'trail') {
        this.x = x;
        this.y = y;
        this.vx = vx;
        this.vy = vy;
        this.life = 1.0;
        this.decay = type === 'sparkle' ? 0.05 : 0.015;
        this.size = type === 'sparkle' ? Math.random() * 2 + 0.5 : Math.random() * 3 + 1;
        this.hue = 320; // Pink hue
        this.saturation = 100;
        this.lightness = type === 'sparkle' ? 90 : 60;
        this.type = type;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.2;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.life -= this.decay;
        this.vx *= 0.98;
        this.vy *= 0.98;
        this.rotation += this.rotationSpeed;
        this.pulsePhase += 0.1;
        
        // Add some gravity for trail particles
        if (this.type === 'trail') {
          this.vy += 0.05;
        }
        
        return this.life > 0;
      }

      draw(ctx) {
        if (this.life <= 0) return;

        const alpha = this.life;
        const size = this.size * this.life;
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        const currentSize = size * pulse;
        
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        if (this.type === 'sparkle') {
          // Draw sparkle shape
          ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${alpha})`;
          ctx.beginPath();
          for (let i = 0; i < 4; i++) {
            const angle = (i * Math.PI) / 2;
            const x1 = Math.cos(angle) * currentSize;
            const y1 = Math.sin(angle) * currentSize;
            const x2 = Math.cos(angle + Math.PI / 4) * currentSize * 0.5;
            const y2 = Math.sin(angle + Math.PI / 4) * currentSize * 0.5;
            
            if (i === 0) ctx.moveTo(x1, y1);
            else ctx.lineTo(x1, y1);
            ctx.lineTo(x2, y2);
          }
          ctx.closePath();
          ctx.fill();
        } else {
          // Create gradient for glow effect
          const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, currentSize * 3);
          
          gradient.addColorStop(0, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${alpha})`);
          gradient.addColorStop(0.5, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${alpha * 0.5})`);
          gradient.addColorStop(1, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, 0)`);

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(0, 0, currentSize * 3, 0, Math.PI * 2);
          ctx.fill();

          // Inner bright core
          ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, 80%, ${alpha * 0.8})`;
          ctx.beginPath();
          ctx.arc(0, 0, currentSize, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      }
    }

    // Enhanced mouse move handler with sparkles
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const newX = e.clientX - rect.left;
      const newY = e.clientY - rect.top;

      // Calculate velocity
      const vx = (newX - mouseRef.current.x) * 0.3;
      const vy = (newY - mouseRef.current.y) * 0.3;

      // Create particles along the path
      const distance = Math.sqrt(vx * vx + vy * vy);
      const particleCount = Math.min(Math.floor(distance / 4), 10);

      for (let i = 0; i < particleCount; i++) {
        const t = i / particleCount;
        const x = mouseRef.current.x + vx * t;
        const y = mouseRef.current.y + vy * t;
        
        // Add some randomness
        const randomVx = vx + (Math.random() - 0.5) * 3;
        const randomVy = vy + (Math.random() - 0.5) * 3;
        
        // Create trail particles
        particlesRef.current.push(new Particle(x, y, randomVx, randomVy, 'trail'));
        
         // Create many more sparkles for magical effect
         if (Math.random() < 0.8) {
           const sparkleVx = (Math.random() - 0.5) * 6;
           const sparkleVy = (Math.random() - 0.5) * 6;
           particlesRef.current.push(new Particle(x, y, sparkleVx, sparkleVy, 'sparkle'));
         }
         
         // Add extra sparkles for more magic
         if (Math.random() < 0.6) {
           const sparkleVx = (Math.random() - 0.5) * 8;
           const sparkleVy = (Math.random() - 0.5) * 8;
           particlesRef.current.push(new Particle(x, y, sparkleVx, sparkleVy, 'sparkle'));
         }
         
         // Even more sparkles for maximum magic
         if (Math.random() < 0.4) {
           const sparkleVx = (Math.random() - 0.5) * 10;
           const sparkleVy = (Math.random() - 0.5) * 10;
           particlesRef.current.push(new Particle(x, y, sparkleVx, sparkleVy, 'sparkle'));
         }
      }

      // Mark mouse as moving
      isMouseMovingRef.current = true;
      if (mouseStopTimeoutRef.current) {
        clearTimeout(mouseStopTimeoutRef.current);
      }
      mouseStopTimeoutRef.current = setTimeout(() => {
        isMouseMovingRef.current = false;
      }, 100);

      lastMouseRef.current = { ...mouseRef.current };
      mouseRef.current = { x: newX, y: newY };
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter(particle => {
        const alive = particle.update();
        if (alive) {
          particle.draw(ctx);
        }
        return alive;
      });

      // Draw main cursor glow with pulsing effect
      if (mouseRef.current.x && mouseRef.current.y) {
        const time = Date.now() * 0.005;
        const pulse = Math.sin(time) * 0.3 + 0.7;
        const glowSize = 25 + pulse * 10;
        
        // Outer glow
        const gradient = ctx.createRadialGradient(
          mouseRef.current.x, mouseRef.current.y, 0,
          mouseRef.current.x, mouseRef.current.y, glowSize
        );
        
        gradient.addColorStop(0, `hsla(320, 100%, 80%, ${0.6 * pulse})`);
        gradient.addColorStop(0.3, `hsla(320, 100%, 70%, ${0.3 * pulse})`);
        gradient.addColorStop(0.7, `hsla(320, 100%, 60%, ${0.15 * pulse})`);
        gradient.addColorStop(1, 'hsla(320, 100%, 50%, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(mouseRef.current.x, mouseRef.current.y, glowSize, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright core with pulsing
        const coreSize = 3 + pulse * 2;
        ctx.fillStyle = `hsla(320, 100%, 90%, ${0.8 * pulse})`;
        ctx.beginPath();
        ctx.arc(mouseRef.current.x, mouseRef.current.y, coreSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Add sparkles around cursor when moving
        if (isMouseMovingRef.current && Math.random() < 0.1) {
          const sparkleX = mouseRef.current.x + (Math.random() - 0.5) * 40;
          const sparkleY = mouseRef.current.y + (Math.random() - 0.5) * 40;
          const sparkleVx = (Math.random() - 0.5) * 2;
          const sparkleVy = (Math.random() - 0.5) * 2;
          particlesRef.current.push(new Particle(sparkleX, sparkleY, sparkleVx, sparkleVy, 'sparkle'));
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    // Start animation
    animate();

    // Add event listeners
    canvas.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousemove', handleMouseMove);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      if (mouseStopTimeoutRef.current) {
        clearTimeout(mouseStopTimeoutRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="cursor-trail-canvas"
      style={{
        background: 'transparent',
        mixBlendMode: 'screen'
      }}
    />
  );
}
